import Accordion from '@/common/components/Accordion';
import DragHandle from '@/common/components/DragHandle';
import SelectController from '@/common/components/SelectController';
import { useDragAndDrop } from '@/common/hooks/useDragAndDrop';
import useDebounce from '@/common/hooks/useDebounce';
import { Mapper } from '@/common/services/mapper';
import { cn } from '@/common/utils/cn';
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview';
import { ReviewButtons } from '@/features/establishmentLeadsFunnel/components/ReviewButtons';
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies';
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer';
import { SpecialFeeItem } from '@/features/privateCompanyLeadsFunnel/components/SpecialFeeItem';
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { PrivateCompanyLeadData, SpecialEntriesData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLead';
import { PrivateCompanyLeadsFunnelUpdateValidationSchema } from '@/features/privateCompanyLeadsFunnel/schemas/update.schema';
import usePrivateCompanyLead from '@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLead';
import { useReorderPrivateCompanyLeadSpecialFees } from '@/features/privateCompanyLeadsFunnel/hooks/useReorderPrivateCompanyLeadSpecialFees';
import Alert from '@/common/components/Alert';
import { AlertCircleIcon } from 'lucide-react';

interface SpecialFeesProps {
  control: Control<PrivateCompanyLeadsFunnelUpdateValidationSchema>;
  errors: FieldErrors<PrivateCompanyLeadsFunnelUpdateValidationSchema>;
  fee_rule?: PrivateCompanyLeadData['fee_rule'];
  createPrivateCompanyLeadSpecialFee?: UseMutateFunction<
    void,
    Error,
    { value: string; type: string; fee_template_id: string; execution_priority?: number; },
    unknown
  >;
  createPrivateCompanyLeadSpecialFeeIsPending?: boolean;
  deletePrivateCompanyLeadSpecialFee?: UseMutateFunction<void, Error, string, unknown>;
  approveSpecialFeesInformationLead?: UseMutateFunction<void, Error, { description?: string; } | undefined, unknown>;
  declineSpecialFeesInformationLead?: UseMutateFunction<void, Error, { description?: string; } | undefined, unknown>;
  disabled?: boolean;
}

export const SpecialFees = ({
  control,
  errors,
  fee_rule,
  createPrivateCompanyLeadSpecialFee,
  createPrivateCompanyLeadSpecialFeeIsPending,
  deletePrivateCompanyLeadSpecialFee,
  approveSpecialFeesInformationLead,
  declineSpecialFeesInformationLead,
  disabled,
}: SpecialFeesProps) => {
  const [specialEntries, setSpecialEntries] = useState(fee_rule?.special_entries || []);

  const { id } = useParams();
  const queryClient = useQueryClient();

  const [searchEstablishment, setSearchEstablishment] = useDebounce('');

  const { isFetching: privateCompanyIsFetching } = usePrivateCompanyLead(id || '', false);

  const { mutate: reorderSpecialFees } = useReorderPrivateCompanyLeadSpecialFees({
    privateCompanyLeadId: id,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', id] }),
  });

  const { getItemProps, getHandleProps, draggedIndex, dragOverIndex } = useDragAndDrop({
    onReorder: (fromIndex, toIndex) => {
      setSpecialEntries((prev) => {
        const newOrder = [...prev];
        const [moved] = newOrder.splice(fromIndex, 1);
        newOrder.splice(toIndex, 0, moved);

        const payload = newOrder
          .map((entry, idx) => ({ entry, idx }))
          .filter(({ entry }) => entry.fee_template)
          .map(({ entry, idx }) => ({ id: entry.id, execution_priority: idx }));

        if (payload.length > 0) {
          reorderSpecialFees(payload);
        }

        return newOrder;
      });
    },
    dragImageClassName: '!bg-yellow-op-2',
  });

  const shouldScrollToNewEntryRef = useRef(false)

  useEffect(() => {
    if (!shouldScrollToNewEntryRef.current) return

    shouldScrollToNewEntryRef.current = false

    const newItem = document.querySelector(`[data-drag-item-index="${specialEntries.length - 1}"]`)

    newItem?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [specialEntries?.length])

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishment, caModuleEnabled: true },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentCompanyTransformer.optionTransformer({
        valueFieldName: 'id',
        addAllEstablishmentOption: true,
      }),
    }),
  });

  const deleteSpecialEntry = ({ id, temporary }: { id: string; temporary?: boolean; }) => {
    if (temporary) {
      setSpecialEntries(specialEntries.filter((entry) => entry.temporary_target_id !== id));
      return;
    }
    deletePrivateCompanyLeadSpecialFee?.(id);
  };

  useEffect(() => {
    if (!privateCompanyIsFetching) {
      const sorted = [...(fee_rule?.special_entries || [])].sort(
        (a, b) => (a?.execution_priority ?? Infinity) - (b?.execution_priority ?? Infinity),
      );
      setSpecialEntries(sorted);
    }
  }, [privateCompanyIsFetching]);

  return (
    <Accordion
      defaultOpen
      className="py-1"
      contentClassName="data-[state=open]:overflow-visible"
      head={
        <div className="flex w-full items-center justify-between mr-4">
          <p>Tarifas especiais</p>

          {approveSpecialFeesInformationLead && declineSpecialFeesInformationLead && (
            <div className="ml-auto">
              <ReviewButtons
                reviewState={fee_rule?.last_review?.state}
                onApprove={approveSpecialFeesInformationLead}
                onDecline={declineSpecialFeesInformationLead}
              />
            </div>
          )}
        </div>
      }
      body={
        <div className="flex flex-col gap-4">
          {fee_rule?.last_review?.state === 'disapproved' && (
            <RequestedReview description={fee_rule?.last_review?.description} />
          )}

          <Alert variant="warning" Icon={AlertCircleIcon} containerClassName="bg-transparent p-0">
            <div className="flex flex-col">
              <p className="text-sm">
                Defina a ordem da aplicação, a mais acima será a tarifa qual será aplicada primeiro.
              </p>

              <p className="text-xs italic font-light">
                Caso a próxima também se aplique ao posto, e esteja configurada para não aplicar quando houver uma
                outra especial, ela não será aplicada.
              </p>
            </div>
          </Alert>

          <SelectController
            control={control}
            name="fee_rule.special_entries"
            placeholder="Pesquisar Estabelecimentos"
            value={null}
            onInputChange={setSearchEstablishment}
            triggerOnChange={(newValue: { label: string; value: string; type: string; }) => {
              shouldScrollToNewEntryRef.current = true

              setSpecialEntries([
                ...specialEntries,
                {
                  id: newValue.value,
                  temporary_target_id: newValue.value,
                  temporary_target_name: newValue.label,
                  temporary_target_type: newValue.type,
                } as SpecialEntriesData,
              ]);
            }}
            options={establishmentOptions || []}
            isDisabled={privateCompanyIsFetching || disabled}
            isLoading={createPrivateCompanyLeadSpecialFeeIsPending}
          />

          {specialEntries?.map((specialEntry, index) => (
            <div
              key={specialEntry.id}
              {...getItemProps(index)}
              className={cn(
                'flex gap-2 items-start rounded-md bg-white-200 border-2 border-transparent transition-colors p-2 ',
                draggedIndex === index && 'opacity-50 bg-yellow-op-2',
                dragOverIndex === index && draggedIndex !== index && 'border-blue-700 border-dashed',
              )}
            >
              <div className="flex items-center gap-1 mt-2">
                <DragHandle
                  {...getHandleProps(index)}
                  disabled={!specialEntry.fee_template || privateCompanyIsFetching || disabled}
                />

                <span className="text-sm text-white-800 min-w-[1ch] text-center tabular-nums">
                  {index}
                </span>
              </div>

              <div className="flex-1">
                <SpecialFeeItem
                  specialEntry={specialEntry}
                  setSpecialEntries={setSpecialEntries as Dispatch<SetStateAction<Partial<SpecialEntriesData>[]>>}
                  index={index}
                  createPrivateCompanyLeadSpecialFee={createPrivateCompanyLeadSpecialFee}
                  createPrivateCompanyLeadSpecialFeeIsPending={createPrivateCompanyLeadSpecialFeeIsPending}
                  deleteSpecialEntry={deleteSpecialEntry}
                  control={control}
                  errors={errors}
                  disabled={disabled}
                />
              </div>
            </div>
          ))}
        </div>
      }
    />
  );
};
